"use client";
import React, { useContext } from "react";
import { Theme, BackgroundColor, CssColor } from '@adobe/leonardo-contrast-colors';
import ColorStyleContext from "@/lib/providers/color-style-privider";
import DynamicIcon from "./DynamicIcon";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { getBaseUrl } from "@/util/get-base-url";
import { borderRadiusShorthand, useLayoutStyle } from "@/lib/providers/layout-style-provider";

export interface ProductTagProps {
    tag: any;
    iconOnly?: boolean;
    nameOnly?: boolean;
    variant?: TagVariant;
    className?: string;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    iconSize?: number;
    /** Multiplier applied to the icon size (e.g. 0.7 for compact 3-column mobile cards). */
    iconScale?: number;
}

type TagVariant = "SOLID" | "BORDERED" | "TRANSPARENT" | "FLAT";

const ProductTagImpl = (props: ProductTagProps) => {
    const { tag, iconOnly, nameOnly, variant, className, onClick } = props;
    const colorStyle = useContext(ColorStyleContext)
    const layoutStyle = useLayoutStyle();

    const tagText = tag.attributes.tagName

    const getSystemColorCode = (color: string, fallback: string = '#ffffff') => {
        const colorKey = color as keyof typeof colorStyle;
        return colorStyle && colorKey in colorStyle ? colorStyle[colorKey] : fallback;
    };

    const baseColor = tag.isBgCustomColor && tag.bgCustomColor ? tag.bgCustomColor : getSystemColorCode(tag.bgColor);
    const textColor = tag.isTextCustomColor && tag.textCustomColor ? tag.textCustomColor : getSystemColorCode(tag.textColor);
	const iconColor = tag.isIconCustomColor && tag.iconCustomColor ? tag.iconCustomColor : getSystemColorCode(tag.iconColor);


    // Create a BackgroundColor for Leonardo
	const bg = new BackgroundColor({
		name: 'main',
		colorKeys: [baseColor as CssColor],
		colorspace: 'LCH',
		ratios: {
			normalText: 4.6,
		}
	});

	const theme = new Theme({
		backgroundColor: bg,
		colors: [bg],
		lightness: 95 // adjust lightness to lighten the background
	});

	const contrastPairs = theme.contrastColorPairs;

    const styles = {
        SOLID: {
            borderRadius: borderRadiusShorthand(layoutStyle),
            backgroundColor: contrastPairs.normalText,
            color: contrastPairs.background
        },
        BORDERED: {
            borderRadius: borderRadiusShorthand(layoutStyle),
            border: `1px solid ${baseColor}`,
            backgroundColor: 'transparent',
            color: baseColor
        },
        TRANSPARENT: {
            backgroundColor: 'transparent',
            color: baseColor
        },
        FLAT: {
            borderRadius: borderRadiusShorthand(layoutStyle),
            backgroundColor: contrastPairs.background,
            color: contrastPairs.normalText
		},
		CUSTOM: {
            borderRadius: borderRadiusShorthand(layoutStyle),
			backgroundColor: baseColor,
			color: textColor,
		}
    }

    const iconScale = props.iconScale ?? 1;
    const baseIconSize = props.iconSize || tag.iconSize;
    const iconSize = baseIconSize != null ? baseIconSize * iconScale : baseIconSize;

    if (!tag.show) return null;

    return (
        <div
            id={tag.id}
            className={cn("product-tag-ctn flex justify-center h-fit min-w-0 max-w-full", className)}
            onClick={onClick}
        >
            <div
                className={`tag flex items-center gap-2 text-xs lg:text-sm min-w-0 max-w-full ${
                    nameOnly === true
                        ? "py-2 px-[12px]"
                        : iconOnly === true
                          ? ""
                          : tag.isDisplayName || tag.variant !== "TRANSPARENT"
                            ? "py-2 px-[12px]"
                            : ""
                }`}
                style={
                    variant
                        ? styles[variant]
                        : iconOnly
                          ? styles.TRANSPARENT
                          : styles[tag.variant as TagVariant] || styles.SOLID
                }
            >
                {tag.iconUrl && !nameOnly ? (
                    <div
                        className='icon-ctn relative'
                        style={{
                            height: `${iconSize}px`,
                            paddingRight: `${iconSize * tag.iconRatio}px`,
                        }}
                    >
                        <Image
                            src={`${getBaseUrl(tag.iconUrl)}.webp`}
                            alt={tagText}
                            className="icon absolute w-full h-full"
                            width={iconSize}
                            height={iconSize}
                            unoptimized
                        />
                    </div>
                ) : tag.iconName && !nameOnly ? (
                    <div
                        className="icon"
                        style={
                            tag.variant === "CUSTOM"
                                ? {
                                      color: iconColor,
                                  }
                                : undefined
                        }>
                        <DynamicIcon
                            name={tag.iconName}
                            size={(tag.iconSize ?? 20) * iconScale}
                        />
                    </div>
                ) : null}
                {(nameOnly || (!iconOnly && tag.isDisplayName)) && (
                    <div className="tag-text min-w-0 truncate">{tagText}</div>
                )}
            </div>
        </div>
    );
};

// Custom props comparator for memoization
const areEqual = (prev: ProductTagProps, next: ProductTagProps) => {
    if (prev === next) return true;
    if (prev.variant !== next.variant) return false;
    if (prev.iconOnly !== next.iconOnly) return false;
    if (prev.nameOnly !== next.nameOnly) return false;
    if (prev.className !== next.className) return false;
    if (prev.onClick !== next.onClick) return false; // identity matters for event handler
    if (prev.iconSize !== next.iconSize) return false;
    if (prev.iconScale !== next.iconScale) return false;

    const pt = prev.tag;
    const nt = next.tag;
    if (pt === nt) return true; // same reference, assume unchanged
    if (!pt || !nt) return false;
    if (pt.id !== nt.id) return false;
    // Compare frequently used visual fields
    if (pt.attributes?.tagName !== nt.attributes?.tagName) return false;
    if (pt.variant !== nt.variant) return false;
    if (pt.isDisplayName !== nt.isDisplayName) return false;
    if (pt.bgCustomColor !== nt.bgCustomColor) return false;
    if (pt.textCustomColor !== nt.textCustomColor) return false;
    if (pt.iconCustomColor !== nt.iconCustomColor) return false;
    if (pt.iconUrl !== nt.iconUrl) return false;
    if (pt.iconName !== nt.iconName) return false;
    if (pt.iconSize !== nt.iconSize) return false;
    if (pt.iconRatio !== nt.iconRatio) return false;
    if (pt.show !== nt.show) return false;
    return true;
};

export const ProductTag = React.memo(ProductTagImpl, areEqual);
export default ProductTag;
